export const SEO_LANGUAGES = ["en", "de", "fr", "it", "es"] as const;

export type SeoLanguage = (typeof SEO_LANGUAGES)[number];

/**
 * The organization's full legal name.
 *
 * This is an *identity* field, not a title component: it is what
 * ``og:site_name`` and the JSON-LD ``Organization`` ``name`` declare, where the
 * value names the publisher rather than competing for a truncation budget.
 * CWS-128 shortened the title suffix and deliberately left this alone — see
 * :data:`TITLE_BRAND`.
 */
export const ORGANIZATION_NAME = "Shelpuk AI Technology Consulting";

/**
 * The brand suffix every non-home ``<title>`` ends with.
 *
 * **Deliberately not** :data:`ORGANIZATION_NAME`. Composed as
 * ``\`${pageTitle} | ${TITLE_BRAND}\``` the legal name spent 35 characters of a
 * ~60-character search-result budget on boilerplate, which is the case Google's
 * own title-link guidance describes when it says it may rewrite a title because
 * "boilerplate text takes up too much of the title". Measured on 2026-09-09 it
 * put 4 of 5 home titles and 20 of 80 sales-page titles past the budget on the
 * suffix alone. CWS-128 shortened it to ``Shelpuk``.
 *
 * **This is the single source.** It was two independent literals — one in
 * ``scripts/generate-static-meta.mjs`` and one in
 * ``src/components/LanguageRouter.tsx`` — with nothing binding them, so the
 * served HTML and the client-side ``<head>`` could disagree after the first
 * in-app navigation. Both now read this constant.
 */
export const TITLE_BRAND = "Shelpuk";

/**
 * The root document's ``<title>`` (CWS-237; supersedes CWS-51's selector
 * title and ``D3`` option (b)'s "action-first" framing).
 *
 * The bare origin serves the prerendered English homepage experience (the
 * CWS-51 head machinery is preserved: self-canonical, six-alternate hreflang,
 * ``x-default → /``), with one owner-driven change: the language selector is
 * gone. The root's title therefore names the **offering** the x-default page
 * carries — the international entry to the same site the locale homes tell —
 * then the brand suffix the site's ``<title>`` convention applies everywhere
 * else (``${pageTitle} | ${TITLE_BRAND}``). It must remain distinct from the
 * ``/en`` home's title so the S14 within-locale-uniqueness sweep does not
 * see a duplicate.
 *
 * Deliberately **not** :data:`ORGANIZATION_NAME`. The legal name is the
 * ``unresolved()`` fallback signature that two guards refuse — the
 * ``routeContracts`` ``I2`` assertion at ``routeContracts.test.ts:698-717`` and
 * ``seoHead.test.ts:414-423`` — and the root is a designed page, not a missing
 * one. A name that does not collide with the fallback is the honest choice.
 */
export const ROOT_DOCUMENT_TITLE = `AI consulting across Europe | ${TITLE_BRAND}`;

/**
 * The root document's ``<meta name="description">`` (CWS-237; supersedes
 * CWS-51's selector copy).
 *
 * Same role it always had: a sentence the root's ``x-default`` page can
 * surface for the search query "shelpuk language" and similar — naming the
 * five supported languages and the offering, without sounding like the locale
 * home's narrative opener. The locale homes' descriptions begin with the
 * phrase "Learn what artificial intelligence is…" (``src/i18n/locales/<lang>.json``
 * ``meta.homeDescription``) — the root's opener differs so the S15
 * within-locale-uniqueness sweep does not see a duplicate.
 */
export const ROOT_DOCUMENT_DESCRIPTION =
  "AI consulting and engineering for regulated industries across Europe — " +
  "published in English, Deutsch, Français, Italiano and Español.";

/**
 * Longest ``<title>`` that survives a desktop search result intact.
 *
 * Google publishes **no** character limit and truncates on rendered width —
 * roughly 580–600 px, which is 50–60 characters depending on the glyphs
 * (verified 2026-09-09 against current guidance). 60 is therefore a convention
 * chosen as a practical proxy, not a documented rule.
 *
 * ⚠️ **Not to be confused with ``TITLE_MAX_LENGTH`` in**
 * ``tests-node/seoHead.test.ts``. That one is a *generator-sanity* band of 300
 * that exists to catch a title composed twice; this one is an *SEO-guidance*
 * budget. The two answer different questions and that file explains at length
 * why narrowing its band to this number would be wrong.
 */
export const SERP_TITLE_MAX_LENGTH = 60;

/**
 * Longest localized blog headline that fits the budget once branded.
 *
 * **Derived, never restated.** A blog ``<title>`` is
 * ``\`${headline} | ${TITLE_BRAND}\```, so the headline's budget is whatever
 * :data:`SERP_TITLE_MAX_LENGTH` leaves after the separator and the brand.
 * Changing :data:`TITLE_BRAND` moves this automatically, which is the point:
 * the two numbers cannot drift apart.
 */
export const BLOG_TITLE_MAX_LENGTH =
  SERP_TITLE_MAX_LENGTH - TITLE_BRAND.length - " | ".length;

export const DEFAULT_OG_IMAGE_PATHS: Record<SeoLanguage, string> = {
  en: "/og-image-en.png",
  de: "/og-image-de.png",
  fr: "/og-image-fr.png",
  it: "/og-image-it.png",
  es: "/og-image-es.png",
};

export const LOCALIZED_OG_IMAGE_PATHS: Record<string, Record<SeoLanguage, string>> = {
  "services/consulting": {
    en: "/og/services/discovery-en.png",
    de: "/og/services/discovery-de.png",
    fr: "/og/services/discovery-fr.png",
    it: "/og/services/discovery-it.png",
    es: "/og/services/discovery-es.png",
  },
  "services/fixed-price": {
    en: "/og/services/fixed-price-en.png",
    de: "/og/services/fixed-price-de.png",
    fr: "/og/services/fixed-price-fr.png",
    it: "/og/services/fixed-price-it.png",
    es: "/og/services/fixed-price-es.png",
  },
  "services/teams": {
    en: "/og/services/teams-en.png",
    de: "/og/services/teams-de.png",
    fr: "/og/services/teams-fr.png",
    it: "/og/services/teams-it.png",
    es: "/og/services/teams-es.png",
  },
  "services/enablement": {
    en: "/og/services/enablement-en.png",
    de: "/og/services/enablement-de.png",
    fr: "/og/services/enablement-fr.png",
    it: "/og/services/enablement-it.png",
    es: "/og/services/enablement-es.png",
  },
  "industries/defence": {
    en: "/og/industries/defence-en.png",
    de: "/og/industries/defence-de.png",
    fr: "/og/industries/defence-fr.png",
    it: "/og/industries/defence-it.png",
    es: "/og/industries/defence-es.png",
  },
  "industries/pharma": {
    en: "/og/industries/pharma-en.png",
    de: "/og/industries/pharma-de.png",
    fr: "/og/industries/pharma-fr.png",
    it: "/og/industries/pharma-it.png",
    es: "/og/industries/pharma-es.png",
  },
  "industries/finance": {
    en: "/og/industries/finance-en.png",
    de: "/og/industries/finance-de.png",
    fr: "/og/industries/finance-fr.png",
    it: "/og/industries/finance-it.png",
    es: "/og/industries/finance-es.png",
  },
  "industries/enterprise": {
    en: "/og/industries/enterprise-en.png",
    de: "/og/industries/enterprise-de.png",
    fr: "/og/industries/enterprise-fr.png",
    it: "/og/industries/enterprise-it.png",
    es: "/og/industries/enterprise-es.png",
  },
  "open-source/kindly-web-search": {
    en: "/og/open-source/open-source-en.png",
    de: "/og/open-source/open-source-de.png",
    fr: "/og/open-source/open-source-fr.png",
    it: "/og/open-source/open-source-it.png",
    es: "/og/open-source/open-source-es.png",
  },
  "open-source/lad-code-review": {
    en: "/og/open-source/open-source-en.png",
    de: "/og/open-source/open-source-de.png",
    fr: "/og/open-source/open-source-fr.png",
    it: "/og/open-source/open-source-it.png",
    es: "/og/open-source/open-source-es.png",
  },
  "open-source/tdd": {
    en: "/og/open-source/open-source-en.png",
    de: "/og/open-source/open-source-de.png",
    fr: "/og/open-source/open-source-fr.png",
    it: "/og/open-source/open-source-it.png",
    es: "/og/open-source/open-source-es.png",
  },
  "sovereign-ai": {
    en: "/og/sovereign-ai-en.png",
    de: "/og/sovereign-ai-de.png",
    fr: "/og/sovereign-ai-fr.png",
    it: "/og/sovereign-ai-it.png",
    es: "/og/sovereign-ai-es.png",
  },
  insights: {
    en: "/og/insights-en.png",
    de: "/og/insights-de.png",
    fr: "/og/insights-fr.png",
    it: "/og/insights-it.png",
    es: "/og/insights-es.png",
  },
};

export const OG_LOCALE_MAP: Record<SeoLanguage, string> = {
  en: "en_US",
  de: "de_DE",
  fr: "fr_FR",
  it: "it_IT",
  es: "es_ES",
};

export function normalizeSeoLanguage(lang?: string | null): SeoLanguage {
  const normalized = lang?.toLowerCase().split("-")[0];
  return SEO_LANGUAGES.includes(normalized as SeoLanguage)
    ? (normalized as SeoLanguage)
    : "en";
}

export function getDefaultOgImagePath(lang?: string | null): string {
  return DEFAULT_OG_IMAGE_PATHS[normalizeSeoLanguage(lang)];
}

export function getLocalizedOgImagePath(
  routePath: string,
  lang?: string | null,
): string | undefined {
  const localized = LOCALIZED_OG_IMAGE_PATHS[routePath];

  if (!localized) {
    return undefined;
  }

  const safeLang = normalizeSeoLanguage(lang);
  return localized[safeLang] || localized.en;
}

export function getOgLocale(lang?: string | null): string {
  return OG_LOCALE_MAP[normalizeSeoLanguage(lang)];
}

export function getAlternateOgLocales(lang?: string | null): string[] {
  const safeLang = normalizeSeoLanguage(lang);
  return SEO_LANGUAGES.filter((value) => value !== safeLang).map(
    (value) => OG_LOCALE_MAP[value],
  );
}
